import React, { useState, useEffect } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';

const DynamicColumns = ({ 
    anchorEl, 
    visible, 
    columns, 
    columnCheckedObject, 
    setColumnCheckedObject, 
    updateDynamicColumns, 
    handleColumnsClose, 
    loading 
}) => {
    const [columnSearchText, setColumnSearchText] = useState("");

    const handleColumnToggle = (key, value) => {
        if (columnCheckedObject && columnCheckedObject[key]) {
            setColumnCheckedObject(prevState => {
                const state = { ...prevState };
                delete state[key];
                return state;
            });
        } else {
            setColumnCheckedObject(prevState => ({
                ...prevState,
                [key]: value
            }));
        }
    };

    useEffect(() => {
        if (columns && columns.user_columns && columns.user_columns.length !== 0) {
            Object.entries(columns.user_columns).map(([key, value]) => (
                setColumnCheckedObject(prevState => ({
                    ...prevState,
                    [key]: value
                }))
            ));
        }
    }, [columns]);

    // Reusable Custom Modern Form Input Theme Standard Styling Object
    const searchFieldStyles = {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: '#FFFFFF',
            transition: 'all 0.2s ease-in-out',
            border: '1px solid #E2E8F0',
            '& fieldset': { border: 'none' },
            '&:hover': {
                borderColor: '#CBD5E1'
            },
            '&.Mui-focused': {
                borderColor: '#1E293B',
                boxShadow: '0 0 0 3px rgba(30, 41, 59, 0.08)'
            }
        },
        '& input': {
            padding: '8px 12px',
            fontSize: '13.5px',
            color: '#1E293B',
            fontWeight: 500
        }
    };

    return (
        <>
            {visible && (
                <Popover
                    id="dynamic-column-positioned-menu"
                    className="dynamic-column-positioned-menu"
                    anchorEl={anchorEl}
                    open={visible}
                    onClose={handleColumnsClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            width: 320,
                            maxHeight: 420,
                            borderRadius: '14px',
                            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)',
                            border: '1px solid #E2E8F0',
                            mt: 1.5,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                >
                    {/* Header Workspace Block */}
                    <Box sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 1.5 }}>
                            Toggle Visible Columns
                        </Typography>
                        <TextField 
                            fullWidth
                            size="small" 
                            variant="outlined"
                            sx={searchFieldStyles}
                            value={columnSearchText}
                            onChange={(event) => setColumnSearchText(event.target.value)} 
                            placeholder="Search active columns..." 
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Divider sx={{ borderColor: '#E2E8F0' }} />

                    {/* Scrollable Column Configuration Selection List Frame */}
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                        <List disablePadding>
                            {columns && columns.default_columns && Object.entries(columns.default_columns).map(([key, value]) => {
                                const labelId = `checkbox-list-secondary-label-${value}`;
                                const isHidden = columnSearchText && columnSearchText !== "" && key.toLowerCase().indexOf(columnSearchText.toLowerCase()) === -1;

                                if (isHidden) return null;

                                return (
                                    <ListItem 
                                        key={key} 
                                        disablePadding
                                        sx={{ 
                                            borderRadius: '8px',
                                            mb: 0.5,
                                            px: 1.5,
                                            py: 0.5,
                                            transition: 'background-color 0.2s',
                                            '&:hover': { bgcolor: '#F1F5F9' }
                                        }}
                                    >
                                        <Switch
                                            edge="start"
                                            size="small"
                                            color="primary"
                                            onChange={() => handleColumnToggle(key, value)}
                                            checked={(columnCheckedObject && columnCheckedObject[key]) ? true : false}
                                            inputProps={{ 'aria-labelledby': labelId }}
                                        />
                                        <ListItemText 
                                            id={labelId} 
                                            primary={key} 
                                            primaryTypographyProps={{
                                                fontSize: '13.5px',
                                                fontWeight: 600,
                                                color: '#334155',
                                                style: { marginLeft: '8px' }
                                            }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                    <Divider sx={{ borderColor: '#E2E8F0', borderStyle: 'dashed' }} />

                    {/* Action Execution Footer Layout Frame */}
                    <Box sx={{ p: 2, bgcolor: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LoadingButton
                            fullWidth
                            variant="outlined"
                            loading={loading}
                            disabled={(columnCheckedObject && Object.keys(columnCheckedObject).length !== 0) ? false : true}
                            onClick={() => updateDynamicColumns(true)}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontWeight: 700,
                                py: 0.8,
                                borderColor: '#E2E8F0',
                                color: '#475569',
                                '&:hover': {
                                    borderColor: '#CBD5E1',
                                    backgroundColor: '#F1F5F9'
                                }
                            }}
                        >
                            Clear
                        </LoadingButton>
                        
                        <LoadingButton 
                            fullWidth
                            variant="contained" 
                            loading={loading} 
                            onClick={() => updateDynamicColumns()}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontWeight: 700,
                                py: 0.8,
                                boxShadow: 'none',
                                backgroundColor: '#1E293B',
                                color: '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#0F172A',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            Apply
                        </LoadingButton>
                    </Box>
                </Popover>
            )}
        </>
    );
}

export default DynamicColumns;